import { createComputed, createSignal, Show } from "solid-js";
import { UserMenu } from "./components/UserMenu";
import { Breadcrumbs } from "./components/Breadcrumbs";
import { Cards } from "./components/Cards";
import SignIn from "./components/SignIn";
import { useLocalStore } from "./context/StateContext";
import { cn } from "@/utilities/cn";
import { useLocation, useParams } from "@solidjs/router";
import Types from "@/types";

// ContextMenu component
const ContextMenu = () => {
  const location = useLocation();
  const params = useParams();
  const LocalStore = useLocalStore();

  // Check if we're in a detail view
  const isDetailView = () => {
    const path = location.pathname;
    return path.includes('/media/') ||
      path.includes('/items/') ||
      path.includes('/tables/');
  };

  // Get the current item type (media, item, table)
  const getItemType = () => {
    const path = location.pathname;
    if (path.includes('/media/')) return 'media';
    if (path.includes('/items/')) return 'item';
    if (path.includes('/tables/')) return 'table';
    return null;
  };

  // Get the current item ID
  const getItemId = () => params.id || '';

  // Handle copying ID to clipboard
  const handleCopyId = () => {
    navigator.clipboard.writeText(getItemId());
    document.getElementById('context-menu-popover')?.hidePopover?.();
  };

  // Handle download action with simplified logic
  const handleDownload = async () => {
    const itemId = getItemId();

    if (getItemType() === 'media') {
      try {
        // Get media metadata from database
        const res = await LocalStore.database.find({
          selector: { origin: itemId }
        });

        const connections = res.docs;
        const mediaData: any = {};

        // Build media data object (same pattern as in MediaDetailView)
        connections.forEach((connection: any) => {
          mediaData[connection.type] = connection.target;
        });

        // Construct file URL like in MediaDetailView
        const serverUrl = `https://media.trettin.org:8443/api/blobs/download/stuff`;
        const mediaUrl = `${serverUrl}/${LocalStore.activeUserId}/${itemId}/${Types.Original}`;

        // Determine filename with proper extension
        let fileName = mediaData.name || `media-${itemId}`;
        if (mediaData.extension) {
          fileName += `.${mediaData.extension}`;
        }

        // Download using File System Access API (no fallback)
        const response = await fetch(mediaUrl);
        const blob = await response.blob();

        // Use File System Access API
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{
            description: 'Media File',
            accept: { '*/*': ['.jpg', '.png', '.mp4', '.pdf', '.mp3'] }
          }]
        });

        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      } catch (err) {
        console.error('Download failed:', err);
      }
    }

    document.getElementById('context-menu-popover')?.hidePopover?.();
  };

  // Handle delete action (show confirmation first)
  const handleDelete = () => {
    document.getElementById('context-menu-popover')?.hidePopover?.();
    document.getElementById('delete-confirm')?.showPopover?.();
  };

  // Handle actual deletion
  const confirmDelete = async () => {
    const itemType = getItemType();
    const itemId = getItemId();

    try {
      if (itemType === 'media') {
        // Find and delete all connections related to this media
        await LocalStore.deleteMedia(itemId)

        // Redirect back to media list
        window.location.href = '/media';
      }
    } catch (err) {
      console.error('Delete failed:', err);
    }

    document.getElementById('delete-confirm')?.hidePopover?.();
  };

  // Download button component - less prominent design
  const DownloadButton = () => (
    <button
      onClick={handleDownload}
      class="hidden md:flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
      title="Download"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="7 10 12 15 17 10"></polyline>
        <line x1="12" y1="15" x2="12" y2="3"></line>
      </svg>
      Download
    </button>
  );

  return (
    <div class="flex items-center gap-2 pr-4">
      {isDetailView() && (
        <>
          {/* Download button in header for larger screens */}
          {getItemType() === 'media' && <DownloadButton />}

          <button
            id="context-menu-button"
            popovertarget="context-menu-popover"
            class="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-300 dark:hover:bg-gray-700"
            style={{
              // @ts-ignore
              "anchor-name": "--context-menu-anchor"
            }}
            aria-label="Options"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="1"></circle>
              <circle cx="12" cy="5" r="1"></circle>
              <circle cx="12" cy="19" r="1"></circle>
            </svg>
          </button>

          <div
            id="context-menu-popover"
            popover="auto"
            class="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
            style={{
              "position": "absolute",
              // @ts-ignore
              "position-anchor": "--context-menu-anchor",
              "position-area": "bottom left", // Position at bottom-left to extend leftward
              "right": "0", // Align the right edge with the anchor
              "z-index": "50"
            }}
          >
            <div class="flex flex-col w-40">
              {/* Download option only visible in popover on small screens */}
              <Show when={getItemType() === 'media'}>
                <button
                  onClick={handleDownload}
                  class="md:hidden flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Download
                </button>
              </Show>
              <button
                onClick={handleDelete}
                class="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                Delete
              </button>
              <button
                onClick={handleCopyId}
                class="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                Copy ID
              </button>
            </div>
          </div>

          {/* Delete confirmation dialog */}
          <div
            id="delete-confirm"
            popover="manual"
            class="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50"
          >
            <h3 class="text-lg font-medium mb-2">Confirm Delete</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete this {getItemType()}? This action cannot be undone.
            </p>
            <div class="flex gap-2">
              <button
                onClick={confirmDelete}
                class="flex-1 px-3 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 cursor-pointer"
              >
                Delete
              </button>
              <button
                onClick={() => document.getElementById('delete-confirm')?.hidePopover?.()}
                class="flex-1 px-3 py-2 text-sm rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Separate SidebarContent component
const SidebarContent = (props: { isCollapsed?: boolean; onToggle?: () => void }) => {
  return (
    <div class={cn("flex flex-col h-full justify-between relative", props.isCollapsed ? 'p-2' : 'p-4')}>
      <Show when={props.onToggle}>
        <button
          onClick={props.onToggle}
          class="absolute top-6 -right-3 w-6 h-6 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 cursor-pointer bg-white dark:bg-gray-800 rounded-full shadow-lg z-10 flex items-center justify-center"
        >
          {props.isCollapsed ? '→' : '←'}
        </button>
      </Show>

      <Cards collapsed={props.isCollapsed} />

      <div class="flex flex-col">
        <hr />
        <UserMenu collapsed={props.isCollapsed} />
      </div>
    </div>
  );
};

const Layout = ({ children }: any) => {
  const LocalStore = useLocalStore()
  const [manuallyCollapsed, setManuallyCollapsed] = createSignal(localStorage.getItem('sidebar-collapsed') === 'true');

  const handleSignOut = () => {
    LocalStore.removeActiveUser();
    document.getElementById('user-actions')?.hidePopover?.();
    document.getElementById('mobile-sidebar')?.hidePopover?.();
    document.getElementById('logout-confirm')?.hidePopover?.();
  }

  const params = useParams()
  const location = useLocation();
  const [itemId, setItemId] = createSignal(params.id)
  createComputed(() => setItemId(params.id))

  return (
    <Show when={LocalStore.activeUserId} fallback={SignIn()}>
      <div class='flex flex-col h-screen overflow-hidden'>
        {/* Fixed Header */}
        <header class='flex flex-row justify-between gap-4 z-30 h-14 bg-gray-200 dark:bg-gray-800 items-center'>
          <Breadcrumbs />
          <ContextMenu />
        </header>

        <div class='@container/main flex flex-1 flex-row overflow-hidden'>
          {/* Desktop Sidebar */}
          <div class="@[1024px]/main:flex hidden z-20 h-full">
            <aside
              class={cn(
                'flex flex-col h-full border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 transition-all duration-300',
                manuallyCollapsed() ? 'w-16' : 'w-64'
              )}
            >
              <SidebarContent
                isCollapsed={manuallyCollapsed()}
                onToggle={() => {
                  localStorage.setItem('sidebar-collapsed', (!manuallyCollapsed()).toString())
                  setManuallyCollapsed(!manuallyCollapsed())
                }}
              />
            </aside>
          </div>

          {/* Tablet/Mobile Sidebar */}
          <div class="@[1024px]/main:hidden block h-full">
            <button
              popovertarget="mobile-sidebar"
              class="fixed right-4 bottom-4 w-12 h-12 rounded-full bg-blue-500 text-white shadow-lg flex items-center justify-center hover:bg-blue-600 z-40"
            >
              ☰
            </button>

            <aside
              id="mobile-sidebar"
              popover="auto"
              class="h-full bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 w-64 fixed inset-y-14 left-0 z-30"
            >
              <SidebarContent />
            </aside>
          </div>

          <main class="flex flex-col flex-1 overflow-hidden">
            {children}
          </main>
        </div>

        {/* Global Sign Out confirmation dialog */}
        <div
          id="logout-confirm"
          popover="manual"
          class="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
          style={{
            "z-index": 999
          }}
        >
          <h3 class="text-lg font-medium mb-2">Confirm Sign Out</h3>
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Are you sure you want to sign out?
          </p>
          <div class="flex gap-2">
            <button
              onClick={handleSignOut}
              class="flex-1 px-3 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 cursor-pointer"
            >
              Sign Out
            </button>
            <button
              onClick={() => document.getElementById('logout-confirm')?.hidePopover?.()}
              class="flex-1 px-3 py-2 text-sm rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Show>
  )
}

export default Layout
